import { defineSseEvent } from '@terra/shared-sse-contracts';
import { TunnelRequirementSchema } from '@terra/shared-contracts-internal-network-tunnel';
import { ConversationSchema } from '@terra/shared-schemas-ai';
import { z } from 'zod';
import { PortalToolExecutePayloadSchema } from './portal-tools/portal-tool.schemas';

export const BACKGROUND_EXECUTION_KIND = [
  'neo',
  'subagent',
  'copilot-follow-up',
] as const;
export type BackgroundExecutionKind =
  (typeof BACKGROUND_EXECUTION_KIND)[number];

const BackgroundExecutionKindSchema = z.enum(BACKGROUND_EXECUTION_KIND);

export const VoyagerScreencastFramePayloadSchema = z.object({
  frame: z.string(),
  frameNumber: z.number(),
  format: z.enum(['jpeg', 'png']),
  executionId: z.string(),
  toolCallId: z.string(),
  streamingId: z.string(),
  timestamp: z.number(),
});

export const VOYAGER_SCREENCAST_FRAME_SSE_EVENT = defineSseEvent({
  type: 'voyager_screencast_frame',
  payload: VoyagerScreencastFramePayloadSchema,
  channelPrefix: 'voyager:screencast',
  channelKey: ({ streamingId, frameNumber }) => `${streamingId}:${frameNumber}`,
});

export const ConversationCreatedPayloadSchema = z.object({
  threadId: z.string(),
  conversation: ConversationSchema.extend({
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
});

export const CONVERSATION_CREATED_SSE_EVENT = defineSseEvent({
  type: 'conversation_created',
  payload: ConversationCreatedPayloadSchema,
  channelPrefix: 'conversation',
  channelKey: ({ threadId }) => threadId,
});

export const ConversationUpdatedPayloadSchema = z.object({
  threadId: z.string(),
  conversation: ConversationSchema.extend({
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
});

export const CONVERSATION_UPDATED_SSE_EVENT = defineSseEvent({
  type: 'conversation_updated',
  payload: ConversationUpdatedPayloadSchema,
  channelPrefix: 'conversation',
  channelKey: ({ threadId }) => threadId,
});

export const TerminalSessionModeSchema = z.enum(['shared', 'isolated']);
export const TerminalPtyActionSchema = z.enum([
  'open',
  'send',
  'read',
  'interrupt',
  'close',
]);

export const TerminalExecutePayloadSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'object' || value === null) {
      return value;
    }

    const record = value as Record<string, unknown>;
    const sessionId =
      typeof record.sessionId === 'string' ? record.sessionId : undefined;
    const chatId =
      typeof record.chatId === 'string' ? record.chatId : undefined;
    const legacyCommand =
      typeof record.command === 'string' ? record.command : undefined;
    const executionCommand =
      typeof record.executionCommand === 'string'
        ? record.executionCommand
        : legacyCommand;

    return {
      ...record,
      chatId: chatId ?? sessionId,
      batchId: typeof record.batchId === 'string' ? record.batchId : sessionId,
      sessionId: sessionId ?? chatId,
      sessionMode: record.sessionMode ?? 'shared',
      isBackground: record.isBackground ?? false,
      displayCommand:
        typeof record.displayCommand === 'string'
          ? record.displayCommand
          : legacyCommand,
      executionCommand,
      command: legacyCommand ?? executionCommand,
      executeInElectronPTY: true,
    };
  },
  z.object({
    toolCallId: z.string(),
    toolName: z.string(),
    displayCommand: z.string(),
    executionCommand: z.string(),
    command: z.string().optional(),
    sessionId: z.string(),
    batchId: z.string(),
    chatId: z.string(),
    sessionMode: TerminalSessionModeSchema,
    isBackground: z.boolean(),
    executeInElectronPTY: z.literal(true),
    cwd: z.string().optional(),
    timeoutMs: z.number().int().positive().optional(),
    shell: z.string().optional(),
    idleTimeoutMs: z.number().int().positive().optional(),
    killOnTimeout: z.boolean().optional(),
    jobId: z.string().optional(),
    tailBytes: z.number().int().positive().optional(),
    ptyId: z.string().optional(),
    ptyAction: TerminalPtyActionSchema.optional(),
    ptyInput: z.string().optional(),
    waitMs: z.number().int().positive().optional(),
    elevated: z.boolean().optional(),
    description: z.string().optional(),
    tunnelRequirement: TunnelRequirementSchema.optional(),
  }),
);

export const PORTAL_TOOL_EXECUTE_SSE_EVENT = defineSseEvent({
  type: 'PORTAL_TOOL_EXECUTE',
  payload: PortalToolExecutePayloadSchema,
  channelPrefix: 'portal-tool',
  channelKey: ({ toolCallId }) => toolCallId,
});

export const TERMINAL_EXECUTE_SSE_EVENT = defineSseEvent({
  type: 'TERMINAL_EXECUTE',
  payload: TerminalExecutePayloadSchema,
  channelPrefix: 'terminal',
  channelKey: ({ toolCallId }) => toolCallId,
});

export const ExternalToolBatchPayloadSchema = z.preprocess(
  (value) => {
    if (typeof value !== 'object' || value === null) {
      return value;
    }

    const record = value as Record<string, unknown>;
    const sessionId =
      typeof record.sessionId === 'string' ? record.sessionId : undefined;

    return {
      ...record,
      chatId: typeof record.chatId === 'string' ? record.chatId : sessionId,
    };
  },
  z.object({
    batchId: z.string(),
    sessionId: z.string().optional(),
    chatId: z.string(),
    resumeGroupId: z.string().optional(),
    toolCallIds: z.array(z.string()),
    toolName: z.string(),
    toolNameByToolCallId: z.record(z.string(), z.string()).optional(),
  }),
);

export const EXTERNAL_TOOL_BATCH_SSE_EVENT = defineSseEvent({
  type: 'EXTERNAL_TOOL_BATCH',
  payload: ExternalToolBatchPayloadSchema,
  channelPrefix: 'external-tool',
  channelKey: ({ batchId }) => batchId,
});

export type VoyagerScreencastFramePayload = z.infer<
  typeof VoyagerScreencastFramePayloadSchema
>;
export type ConversationCreatedPayload = z.infer<
  typeof ConversationCreatedPayloadSchema
>;
export type ConversationUpdatedPayload = z.infer<
  typeof ConversationUpdatedPayloadSchema
>;
export type ConversationCreatedConversation =
  ConversationCreatedPayload['conversation'];
export type TerminalSessionMode = z.infer<typeof TerminalSessionModeSchema>;
export type TerminalExecutePayload = z.infer<
  typeof TerminalExecutePayloadSchema
>;
export type ExternalToolBatchPayload = z.infer<
  typeof ExternalToolBatchPayloadSchema
>;

type BuildTerminalExecutePayloadInput = Omit<
  TerminalExecutePayload,
  'command' | 'executeInElectronPTY' | 'isBackground'
> & {
  isBackground?: boolean;
};

export function buildTerminalExecutePayload({
  toolCallId,
  toolName,
  displayCommand,
  executionCommand,
  sessionId,
  batchId,
  chatId,
  sessionMode,
  isBackground = false,
  cwd,
  timeoutMs,
  shell,
  idleTimeoutMs,
  killOnTimeout,
  jobId,
  tailBytes,
  ptyId,
  ptyAction,
  ptyInput,
  waitMs,
  elevated,
  description,
  tunnelRequirement,
}: BuildTerminalExecutePayloadInput): TerminalExecutePayload {
  return TerminalExecutePayloadSchema.parse({
    toolCallId,
    toolName,
    displayCommand,
    executionCommand,
    command: executionCommand,
    sessionId,
    batchId,
    chatId,
    sessionMode,
    isBackground,
    executeInElectronPTY: true,
    cwd,
    timeoutMs,
    shell,
    idleTimeoutMs,
    killOnTimeout,
    jobId,
    tailBytes,
    ptyId,
    ptyAction,
    ptyInput,
    waitMs,
    elevated,
    description,
    tunnelRequirement,
  });
}

export type TerminalPtyAction = z.infer<typeof TerminalPtyActionSchema>;

export const BackgroundExecutionStartedPayloadSchema = z.object({
  copilotThreadId: z.string(),
  agentThreadId: z.string(),
  toolCallId: z.string(),
  applicationId: z.string(),
  agentType: z.string(),
  kind: BackgroundExecutionKindSchema.optional(),
  title: z.string().optional(),
});

export const BACKGROUND_EXECUTION_STARTED_SSE_EVENT = defineSseEvent({
  type: 'BACKGROUND_EXECUTION_STARTED',
  payload: BackgroundExecutionStartedPayloadSchema,
  channelPrefix: 'background-execution',
  channelKey: ({ copilotThreadId }) => copilotThreadId,
});

export const BackgroundExecutionCompletedPayloadSchema = z.object({
  copilotThreadId: z.string(),
  agentThreadId: z.string(),
  applicationId: z.string(),
  agentType: z.string(),
  kind: BackgroundExecutionKindSchema.optional(),
});

export const BACKGROUND_EXECUTION_COMPLETED_SSE_EVENT = defineSseEvent({
  type: 'BACKGROUND_EXECUTION_COMPLETED',
  payload: BackgroundExecutionCompletedPayloadSchema,
  channelPrefix: 'background-execution',
  channelKey: ({ copilotThreadId }) => copilotThreadId,
});

export type BackgroundExecutionStartedPayload = z.infer<
  typeof BackgroundExecutionStartedPayloadSchema
>;

export type BackgroundExecutionCompletedPayload = z.infer<
  typeof BackgroundExecutionCompletedPayloadSchema
>;

export const ToolCallProgressPayloadSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  type: z.enum([
    'data-toolCallUpdate',
    'data-toolCallStart',
    'data-toolCallComplete',
    'data-toolCallError',
  ]),
  status: z.enum([
    'started',
    'in-progress',
    'error',
    'completed',
    'result',
    'aborted',
  ]),
  message: z.string(),
  timestamp: z.number(),
  id: z.string().optional(),
  labelPresent: z.string().optional(),
  labelPast: z.string().optional(),
  error: z.string().optional(),
  icon: z.string().optional(),
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  timing: z
    .object({
      durationMs: z.number(),
      startedAt: z.string(),
      completedAt: z.string(),
    })
    .optional(),
  fileMetadata: z
    .object({
      codeWithSecretsPath: z.string().optional(),
      codeResultPath: z.string().optional(),
      intelligenceResponsePath: z.string().optional(),
      filePaths: z.array(z.string()).optional(),
    })
    .optional(),
  progressEvents: z
    .array(
      z.object({
        id: z.string(),
        stage: z.string(),
        detail: z.string().optional(),
        timestamp: z.number(),
      }),
    )
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const TOOL_CALL_PROGRESS_SSE_EVENT = defineSseEvent({
  type: 'TOOL_CALL_PROGRESS',
  payload: ToolCallProgressPayloadSchema,
  channelPrefix: 'tool-call-progress',
  channelKey: ({ toolCallId }) => toolCallId,
});

export type ToolCallProgressPayload = z.infer<
  typeof ToolCallProgressPayloadSchema
>;
